import React, { useEffect, useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { 
  Book, Eye, Star, Clock, User, Bookmark, BookmarkCheck,
  ChevronRight, List, Share2
} from 'lucide-react';
import { novelAPI, chapterAPI } from '../../services/api';
import { useAuthStore } from '../../hooks/useStore';
import AdDisplay from '../../components/AdDisplay/AdDisplay';
import toast from 'react-hot-toast';
import './NovelDetail.css';

const NovelDetail = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { isAuthenticated, isBookmarked, addBookmark, removeBookmark } = useAuthStore();
  
  const [novel, setNovel] = useState(null);
  const [chapters, setChapters] = useState([]);
  const [relatedNovels, setRelatedNovels] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('info');
  const [showAllChapters, setShowAllChapters] = useState(false);

  useEffect(() => {
    const fetchNovel = async () => {
      setIsLoading(true);
      try {
        const response = await novelAPI.getBySlug(slug);
        setNovel(response.data.novel);
        setRelatedNovels(response.data.relatedNovels || []);
        
        // Set document title
        if (response.data.novel?.title) {
          document.title = `${response.data.novel.title} - HotNovel`;
        }
        
        if (response.data.novel?._id) {
          const chaptersRes = await chapterAPI.getList(response.data.novel._id, { limit: 50 });
          setChapters(chaptersRes.data || []);
        }
      } catch (error) {
        toast.error('Failed to load novel info');
        navigate('/');
      } finally {
        setIsLoading(false);
      }
    };
    
    fetchNovel();
    window.scrollTo(0, 0);
  }, [slug, navigate]);

  const handleBookmark = async () => {
    if (!isAuthenticated) {
      toast.error('Please login to bookmark');
      navigate('/login');
      return;
    }
    
    if (isBookmarked(novel._id)) {
      await removeBookmark(novel._id);
      toast.success('Removed from bookmarks');
    } else {
      await addBookmark(novel._id);
      toast.success('Added to bookmarks');
    }
  };

  const handleShare = async () => {
    const url = window.location.href;
    if (navigator.share) {
      try {
        await navigator.share({ title: novel.title, url });
      } catch (err) {}
    } else {
      navigator.clipboard.writeText(url);
      toast.success('Link copied');
    }
  };

  const formatViews = (views) => {
    if (!views) return '0';
    if (views >= 1000000) return (views / 1000000).toFixed(1) + 'M';
    if (views >= 1000) return (views / 1000).toFixed(1) + 'K';
    return views.toString();
  };

  const getStatusLabel = (status) => {
    const labels = {
      ongoing: 'Ongoing',
      completed: 'Completed',
      hiatus: 'On Hiatus',
      dropped: 'Dropped'
    };
    return labels[status] || status;
  };

  const getStatusClass = (status) => {
    const classes = {
      ongoing: 'status-ongoing',
      completed: 'status-completed',
      hiatus: 'status-hiatus',
      dropped: 'status-dropped'
    };
    return classes[status] || '';
  };

  if (isLoading) {
    return (
      <main className="novel-detail-page">
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <p>Loading...</p>
        </div>
      </main>
    );
  }

  if (!novel) return null;

  const displayedChapters = showAllChapters ? chapters : chapters.slice(0, 20);

  return (
    <main className="novel-detail-page">
      {/* SEO Meta Tags */}
      <Helmet>
        <title>{novel.title} - HotNovel</title>
        <meta name="description" content={novel.description?.substring(0, 160) || `Read ${novel.title} online for free`} />
        <meta property="og:title" content={`${novel.title} - HotNovel`} />
        <meta property="og:description" content={novel.description?.substring(0, 160)} />
        {novel.cover && <meta property="og:image" content={novel.cover} />}
        <link rel="canonical" href={`https://hotnovel.me/novel/${novel.slug}`} />
      </Helmet>

      {/* Ad - Novel Top */}
      <section className="novel-hero">
        
        <AdDisplay position="novel_top" className="novel-top-ad" />
        <div className="hero-content">
          <div className="novel-cover">
            {novel.cover && novel.cover !== 'default-cover.jpg' ? (
              <img src={novel.cover} alt={novel.title} />
            ) : (
              <div className="cover-placeholder">
                <Book size={48} />
              </div>
            )}
          </div>
          
          <div className="novel-info">
            <div className="novel-badges">
              <span className={`status-badge ${getStatusClass(novel.status)}`}>
                {getStatusLabel(novel.status)}
              </span>
              {novel.isHot && <span className="hot-badge">🔥 HOT</span>}
            </div>
            
            <h1 className="novel-title">{novel.title}</h1>
            
            <div className="novel-author">
              <User size={16} />
              <span>Author: <strong>{novel.author}</strong></span>
            </div>
            
            <div className="novel-genres">
              {novel.genres?.map((genre, idx) => (
                <Link 
                  key={genre._id || idx} 
                  to={`/genre/${genre.slug}`}
                  className="genre-tag"
                >
                  {genre.icon} {genre.name}
                </Link>
              ))}
            </div>
            
            <div className="novel-stats">
              <div className="stat">
                <Book size={18} />
                <span>{novel.chapterCount} chapters</span>
              </div>
              <div className="stat">
                <Eye size={18} />
                <span>{formatViews(novel.stats?.views)} views</span>
              </div>
              <div className="stat">
                <Star size={18} fill="currentColor" />
                <span>{novel.rating?.average?.toFixed(1) || '0'} ({novel.rating?.count || 0})</span>
              </div>
              <div className="stat">
                <Bookmark size={18} />
                <span>{formatViews(novel.stats?.bookmarks)} bookmarks</span>
              </div>
            </div>
            
            <div className="novel-actions">
              <Link to={`/novel/${slug}/chapter/1`} className="btn-primary">
                <Book size={18} />
                Start Reading
              </Link>
              
              {novel.latestChapter && (
                <Link to={`/novel/${slug}/chapter/${novel.chapterCount}`} className="btn-secondary">
                  Latest Chapter
                  <ChevronRight size={18} />
                </Link>
              )}
              
              <button 
                className={`btn-icon ${isBookmarked(novel._id) ? 'active' : ''}`}
                onClick={handleBookmark}
              >
                {isBookmarked(novel._id) ? <BookmarkCheck size={20} /> : <Bookmark size={20} />}
              </button>
              
              <button className="btn-icon" onClick={handleShare}>
                <Share2 size={20} />
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Ad - Novel Middle */}
      <AdDisplay position="novel_middle" className="novel-middle-ad" />

      <section className="novel-tabs-section">
        <div className="tabs-header">
          <button className={`tab ${activeTab === 'info' ? 'active' : ''}`} onClick={() => setActiveTab('info')}>
            About
          </button>
          <button className={`tab ${activeTab === 'chapters' ? 'active' : ''}`} onClick={() => setActiveTab('chapters')}>
            <List size={16} />
            Chapters ({novel.chapterCount})
          </button>
        </div>

        <div className="tabs-content">
          {activeTab === 'info' && (
            <div className="tab-panel info-panel">
              <div className="description">
                <h3>Synopsis</h3>
                <div className="description-text">
                  {novel.description?.split('\n').map((para, idx) => (
                    <p key={idx}>{para}</p>
                  ))}
                </div>
              </div>
              
              {novel.tags?.length > 0 && (
                <div className="tags">
                  <h4>Tags:</h4>
                  <div className="tag-list">
                    {novel.tags.map((tag, idx) => (
                      <span key={idx} className="tag">{tag}</span>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          {activeTab === 'chapters' && (
            <div className="tab-panel chapters-panel">
              <div className="chapters-list">
                {displayedChapters.map((chapter) => (
                  <Link
                    key={chapter._id}
                    to={`/novel/${slug}/chapter/${chapter.chapterNumber}`}
                    className="chapter-item"
                  >
                    <span className="chapter-number">Chapter {chapter.chapterNumber}</span>
                    <span className="chapter-title">{chapter.title?.replace(/^Chapter \d+:?\s*/i, '')}</span>
                    <span className="chapter-date">
                      <Clock size={14} />
                      {new Date(chapter.createdAt).toLocaleDateString('en-US')}
                    </span>
                  </Link>
                ))}
              </div>
              
              {chapters.length > 20 && !showAllChapters && (
                <button className="btn-show-more" onClick={() => setShowAllChapters(true)}>
                  View all {chapters.length} chapters
                </button>
              )}
            </div>
          )}
        </div>
      </section>

      {relatedNovels.length > 0 && (
        <section className="related-section">
          <h3>Related Novels</h3>
          <div className="related-grid">
            {relatedNovels.slice(0, 6).map((item) => (
              <Link key={item._id} to={`/novel/${item.slug}`} className="related-item">
                <div className="related-cover">
                  {item.cover ? (
                    <img src={item.cover} alt={item.title} />
                  ) : (
                    <div className="cover-placeholder small">
                      <Book size={24} />
                    </div>
                  )}
                </div>
                <div className="related-info">
                  <h4>{item.title}</h4>
                  <span>{item.chapterCount} chapters</span>
                </div>
              </Link>
            ))}
          </div>
        </section>
      )}

      {/* Ad - Novel Bottom */}
      <AdDisplay position="novel_bottom" className="novel-bottom-ad" />

      {/* Ad - Sidebar */}
      <AdDisplay position="sidebar" className="novel-sidebar-ad" />
    </main>
  );
};

export default NovelDetail;